"use client";

import { useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useAuth } from "@/lib/context/AuthContext";
import { useAuthModal } from "@/lib/context/AuthModalContext";
import { useCooldown } from "@/hooks/useCooldown";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/;
const COOLDOWN_SEC = 60;

function SignInForm({
  onSuccess,
  onForgot,
}: {
  onSuccess: () => void;
  onForgot: () => void;
}) {
  const t = useTranslations("auth");
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const fn = useCallback(async () => {
    await signIn(email, password);
    onSuccess();
  }, [signIn, email, password, onSuccess]);
  const { submit, error, loading } = useFormSubmit(fn);

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="signin-email">{t("email")}</Label>
        <Input
          id="signin-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="signin-password">{t("password")}</Label>
        <Input
          id="signin-password"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {error && <p className="text-xs text-raise">{error}</p>}
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? t("signingIn") : t("signInButton")}
      </Button>
      <p className="text-center">
        <button
          type="button"
          onClick={onForgot}
          className="text-xs text-fg-dim hover:text-fg underline underline-offset-2"
        >
          {t("forgotPassword")}
        </button>
      </p>
    </form>
  );
}

function RegisterForm() {
  const t = useTranslations("auth");
  const { signUp } = useAuth();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmationSent, setConfirmationSent] = useState(false);
  const fn = useCallback(async () => {
    const { needsConfirmation } = await signUp(email, password, username);
    if (needsConfirmation) setConfirmationSent(true);
  }, [signUp, email, password, username]);
  const { submit, error, setError, loading } = useFormSubmit(fn);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!USERNAME_RE.test(username)) {
      setError(t("usernameHint"));
      return;
    }
    submit();
  }

  if (confirmationSent) {
    return (
      <p className="py-6 text-center text-sm text-fg-dim">{t("checkEmail")}</p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="register-email">{t("email")}</Label>
        <Input
          id="register-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="register-username">{t("username")}</Label>
        <Input
          id="register-username"
          type="text"
          required
          autoComplete="username"
          minLength={3}
          maxLength={20}
          pattern="^[a-zA-Z0-9_]{3,20}$"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <p className="text-[11px] text-fg-mute">{t("usernameHint")}</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="register-password">{t("password")}</Label>
        <Input
          id="register-password"
          type="password"
          required
          autoComplete="new-password"
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {error && <p className="text-xs text-raise">{error}</p>}
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? t("registering") : t("registerButton")}
      </Button>
    </form>
  );
}

function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const t = useTranslations("auth");
  const { resetPassword } = useAuth();
  const cooldown = useCooldown(COOLDOWN_SEC);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const fn = useCallback(async () => {
    await resetPassword(email);
    setSent(true);
  }, [resetPassword, email]);
  const { submit: formSubmit, error, loading: pending } = useFormSubmit(fn);

  async function submit() {
    await formSubmit();
    cooldown.start();
  }

  if (sent) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="text-base font-semibold text-fg">{t("resetSentTitle")}</h2>
        <p className="text-sm text-fg-dim">
          {t("resetSentDescription", { email })}
        </p>
        <Button
          variant="ghost"
          className="text-sm text-accent hover:underline"
          disabled={pending || cooldown.active}
          onClick={submit}
        >
          {cooldown.active
            ? t("resendAvailable", { seconds: cooldown.remaining })
            : t("resendLink")}
        </Button>
        <div>
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-accent hover:underline"
          >
            {t("backToSignIn")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
      <h2 className="text-base font-semibold text-fg">{t("resetPassword")}</h2>
      <p className="text-sm text-fg-dim">{t("resetDescription")}</p>

      {error && <p className="text-xs text-raise">{error}</p>}

      <div className="space-y-1.5">
        <Label htmlFor="forgot-email">{t("email")}</Label>
        <Input
          id="forgot-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <Button type="submit" className="w-full" disabled={pending || cooldown.active}>
        {pending
          ? t("sending")
          : cooldown.active
            ? t("waitSeconds", { seconds: cooldown.remaining })
            : t("sendResetLink")}
      </Button>

      <p className="text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-fg-dim hover:text-fg underline underline-offset-2"
        >
          {t("backToSignIn")}
        </button>
      </p>
    </form>
  );
}

type ModalView = "auth" | "forgot";

export function AuthModal() {
  const t = useTranslations("auth");
  const { isOpen, defaultTab, closeAuthModal } = useAuthModal();
  const { user } = useAuth();
  const [view, setView] = useState<ModalView>("auth");

  function handleOpenChange(open: boolean) {
    if (!open) {
      closeAuthModal();
      setView("auth");
    }
  }

  if (user && isOpen) {
    closeAuthModal();
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {view === "forgot" ? t("resetPassword") : t("signInTab")}
          </DialogTitle>
        </DialogHeader>

        {view === "forgot" ? (
          <ForgotPasswordForm onBack={() => setView("auth")} />
        ) : (
          <Tabs defaultValue={defaultTab}>
            <TabsList className="w-full">
              <TabsTrigger value="signin" className="flex-1">
                {t("signInTab")}
              </TabsTrigger>
              <TabsTrigger value="register" className="flex-1">
                {t("registerTab")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <SignInForm
                onSuccess={closeAuthModal}
                onForgot={() => setView("forgot")}
              />
            </TabsContent>
            <TabsContent value="register">
              <RegisterForm />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
